import React, { useState, useEffect } from 'react';
import api from '@api';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import './TeamAuth.css';
import Breadcrumbs from '../../../components/Breadcrumbs';

function TeamAuth() {
  const { season_id, league_id, team_id } = useParams();
  const { isAuthenticated, loading } = useAuth();
  const [team, setTeam] = useState({ players: [], games: [] });
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [playerSearch, setPlayerSearch] = useState('');

  const domain = useSearchParams()[0].get("domain");
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [playerForm, setPlayerForm] = useState({ firstName: '', lastName: '', image: null, existingPlayerId: '' });
  const [allPlayers, setAllPlayers] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    id: '',
    firstName: '',
    lastName: '',
    image: null,
  });



  useEffect(() => {
    if (loading) return;
    const fetchTeam = async () => {
      try {
        let response;
        if (isAuthenticated) {
          response = await api.get(`/api/seasonsv2/${season_id}/leagues/${league_id}/teams/${team_id}`, {
            withCredentials: true,
          });
        } else if (domain) {
          response = await api.get(`/api/seasonsv2/${season_id}/leagues/${league_id}/teams/${team_id}?domain=${domain}`);
        } else {
          return setError("Unauthorized access");
        }
        setTeam(response.data.team || { players: [], games: [] });
      } catch (err) {
        console.error("Error fetching team:", err.response?.data || err.message);
        setError("Failed to fetch team");
      }
    };
    fetchTeam();
  }, [team_id, season_id, league_id, domain, isAuthenticated, loading]);

  // useEffect(() => {
  //   const fetchAllPlayers = async () => {
  //     try {
  //       const res = await api.get('/api/playersv2', { withCredentials: true });
  //       setAllPlayers(res.data.players);
  //     } catch (err) {
  //       console.error("Error fetching all players:", err);
  //     }
  //   };
  //   if (isAuthenticated) fetchAllPlayers();
  // }, [isAuthenticated]);




  const handleAddPlayer = async (e) => {
    e.preventDefault();
    try {
      if (playerForm.existingPlayerId) {
        // Add existing player to roster
        await api.post(`/api/seasonsv2/${season_id}/leagues/${league_id}/teams/${team_id}/players`, {
          player_id: playerForm.existingPlayerId,
        }, { withCredentials: true });


        const addedPlayer = allPlayers.find(p => p.id === parseInt(playerForm.existingPlayerId));
        setTeam(prev => ({ ...prev, players: [...prev.players, addedPlayer] }));
      } else {
        const formData = new FormData();
        formData.append("firstName", playerForm.firstName);
        formData.append("lastName", playerForm.lastName);
        if (playerForm.image) formData.append("image", playerForm.image);

        // ✅ Create and add player to team in ONE STEP
        const res = await api.post(`/api/seasonsv2/${season_id}/leagues/${league_id}/teams/${team_id}/players`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        });
        console.log("👤 Player Created & Added:", res.data.player);

        // ✅ Re-fetch team to ensure latest players
        const refreshedTeam = await api.get(`/api/seasonsv2/${season_id}/leagues/${league_id}/teams/${team_id}`, {
          withCredentials: true,
        });
        setTeam(refreshedTeam.data.team || { players: [], games: [] });
      }

      setIsModalOpen(false);
      setPlayerForm({ firstName: '', lastName: '', image: null, existingPlayerId: '' });
      setMessage("Player added to team");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error adding player:", error);
      if (error.response?.data?.message === "Player already on this team") {
        setMessage("Player already exists in this team");
        setIsModalOpen(false);
        setPlayerForm({ firstName: '', lastName: '', image: null, existingPlayerId: '' });
      } else {
        setMessage("Error adding player");
      }
      setTimeout(() => setMessage(""), 3000);
    }
  };
  const handleOpenAddPlayerModal = async () => {
    if (isAuthenticated && allPlayers.length === 0) {
      try {
        const res = await api.get('/api/playersv2', { withCredentials: true });
        setAllPlayers(res.data.players);
      } catch (err) {
        console.error("Error fetching all players:", err);
      }
    }
    setIsModalOpen(true);
  };

  const handleDeletePlayers = async () => {
    if (!selectedPlayers.length) return alert("Please select at least one player to delete.");
    if (!window.confirm("Are you sure you want to delete the selected players?")) return;
    try {
      await Promise.all(
        selectedPlayers.map(id =>
          api.delete(`/api/seasonsv2/${season_id}/leagues/${league_id}/teams/${team_id}/players/${id}`, {
            withCredentials: true,
          })
        )
      );
      setTeam(prev => ({ ...prev, players: prev.players.filter(p => !selectedPlayers.includes(p.id)) }));
      setSelectedPlayers([]);
      setMessage("Players deleted");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error deleting players:", err);
    }
  };

  const openEditModal = (player) => {
    setEditForm({
      id: player.id,
      firstName: player.firstName || '',
      lastName: player.lastName || '',
      image: null, // won't preload the file input
    });
    setIsEditModalOpen(true);
  };

  const handleEditPlayer = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append("firstName", editForm.firstName);
      formData.append("lastName", editForm.lastName);
      if (editForm.image) {
        formData.append("image", editForm.image); // ✅ only append if selected
      }

      const res = await api.put(`/api/playersv2/${editForm.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });

      setTeam(prev => ({
        ...prev,
        players: prev.players.map(p =>
          p.id === res.data.player.id ? res.data.player : p
        )
      }));

      setIsEditModalOpen(false);
      setMessage("Player updated");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error updating player:", err);
      setMessage("Error updating player");
    }
  };

  console.log(team)

  const sortedGames = [...team.games].sort((a, b) => {
    const dateA = new Date(`${a.date} ${a.time}`);
    const dateB = new Date(`${b.date} ${b.time}`);
    return dateA - dateB;
  });



  return (
    <div className="team_auth">
      <Breadcrumbs />
      {/* <h2 className="team_auth_title">Team {team.name}</h2> */}

      <div className="teamAuth-btn-container">
        <button className="delete-teamAuth-btn" onClick={handleDeletePlayers}>🗑️</button>
        <button className="add-teamAuth-btn" onClick={handleOpenAddPlayerModal}>
          + Add Player
        </button>

      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Add Player</h3>
            <form onSubmit={handleAddPlayer}>
              {/* <select
                value={playerForm.existingPlayerId}
                onChange={(e) => setPlayerForm({ ...playerForm, existingPlayerId: e.target.value })}
              >
                <option value="">-- Select Existing Player --</option>
                {[...allPlayers]
                  .sort((a, b) => a.firstName.localeCompare(b.firstName))
                  .map((p) => {
                    const teamNames = [...new Set(
                      p.rosters?.map((r) => r.team?.name).filter(Boolean)
                    )].join(", ");

                    return (
                      <option key={p.id} value={p.id}>
                        {p.firstName} {p.lastName} {teamNames ? `(${teamNames})` : ""}
                      </option>
                    );
                  })}

                  
              </select> */}

              <input
                type="text"
                placeholder="Search Existing Player..."
                value={playerSearch}
                onChange={(e) => {
                  setPlayerSearch(e.target.value);
                  setPlayerForm(prev => ({ ...prev, existingPlayerId: '' })); // clear previous selection
                }}
              />

              {playerSearch && (
                <div className="search-results-dropdown">
                  {allPlayers
                    .filter(p =>
                      `${p.firstName} ${p.lastName}`.toLowerCase().includes(playerSearch.toLowerCase())
                    )
                    .slice(0, 10)
                    .map((p) => {
                      const teamNames = [...new Set(
                        p.rosters?.map((r) => r.team?.name).filter(Boolean)
                      )].join(", ");
                      return (
                        <div
                          key={p.id}
                          className="search-result-item"
                          onClick={() => {
                            setPlayerForm(prev => ({ ...prev, existingPlayerId: p.id }));
                            setPlayerSearch(`${p.firstName} ${p.lastName}`); // ✅ show name in input
                          }}

                        >
                          {p.firstName} {p.lastName} {teamNames ? `(${teamNames})` : ""}
                        </div>
                      );
                    })}
                </div>
              )}
              <select
                value={playerForm.existingPlayerId}
                onChange={(e) => {
                  const selected = allPlayers.find(p => p.id === parseInt(e.target.value));
                  if (selected) {
                    setPlayerForm({
                      ...playerForm,
                      existingPlayerId: selected.id,
                      firstName: '',
                      lastName: '',
                    });
                    setPlayerSearch(`${selected.firstName} ${selected.lastName}`);
                  }
                }}
              >
                <option value="">-- Or select from list --</option>
                {[...allPlayers]
                  .sort((a, b) => a.firstName.localeCompare(b.firstName))
                  .map((p) => {
                    const teamNames = [...new Set(p.rosters?.map(r => r.team?.name).filter(Boolean))].join(", ");
                    return (
                      <option key={p.id} value={p.id}>
                        {p.firstName} {p.lastName} {teamNames ? `(${teamNames})` : ""}
                      </option>
                    );
                  })}
              </select>

              <p style={{ textAlign: 'center', margin: '0.5rem 0' }}>— or create a new one —</p>
              <input type="text" placeholder="First Name" value={playerForm.firstName} onChange={(e) => setPlayerForm({ ...playerForm, firstName: e.target.value })} />
              <input type="text" placeholder="Last Name" value={playerForm.lastName} onChange={(e) => setPlayerForm({ ...playerForm, lastName: e.target.value })} />
              <input type="file" accept="image/*" onChange={(e) => setPlayerForm({ ...playerForm, image: e.target.files[0] })} />
              <button type="submit">Add</button>
              <button type="button" onClick={() => setIsModalOpen(false)}>Cancel</button>
            </form>
          </div>
        </div>
      )}

      {isEditModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Edit Player</h3>
            <form onSubmit={handleEditPlayer}>
              <input
                type="text"
                placeholder="First Name"
                value={editForm.firstName}
                onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
              />
              <input
                type="text"
                placeholder="Last Name"
                value={editForm.lastName}
                onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
              />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setEditForm({ ...editForm, image: e.target.files[0] })}
              />
              <button type="submit">Update</button>
              <button type="button" onClick={() => setIsEditModalOpen(false)}>Cancel</button>
            </form>
          </div>
        </div>
      )}

      <table className="teamAuthPlayer-table">
        <thead>
          <tr>
            <th><input type="checkbox" checked={selectedPlayers.length === team.players.length} onChange={() => setSelectedPlayers(selectedPlayers.length === team.players.length ? [] : team.players.map(p => p.id))} /></th>
            <th>Name</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {team.players.map((player) => (
            <tr key={player.id}>
              <td><input type="checkbox" checked={selectedPlayers.includes(player.id)} onChange={() => setSelectedPlayers(prev => prev.includes(player.id) ? prev.filter(id => id !== player.id) : [...prev, player.id])} /></td>
              {/* <td><NavLink to={`/players/${player.id}`}>{player.firstName} {player.lastName}</NavLink></td> */}
              <td>{player.firstName} {player.lastName}</td>
              <td>
                <button onClick={() => openEditModal(player)}>✏️</button>
              </td>

              <td></td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="team-games-container">
        <table className="teamAuthGame-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Date</th>
              <th>Matchup</th>
              <th>Time</th>
              <th>Results</th>
            </tr>
          </thead>
          <tbody>
            {sortedGames.map((game, index) => (
              <tr key={game.id || index} onClick={() => navigate(`/games/${game.id}`)} style={{ cursor: 'pointer'}}>
                <th>{index + 1}</th>
                <td>{game.date}</td>
                <td>{game.team1} vs {game.team2}</td>
                <td>{game.time}</td>
                <td>{game.score_team1} - {game.score_team2}</td>
              </tr>

            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TeamAuth;
